import React, { useState } from "react";
import client from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { toast } from "sonner";
import { Check, Key, Wallet as WalletIcon, X, Landmark, Link2 } from "lucide-react";

export default function Settings() {
  const { user, refresh } = useAuth();
  const { t } = useLang();
  const [binance, setBinance] = useState({ api_key: "", api_secret: "" });
  const [coinbase, setCoinbase] = useState({ api_key: "", api_secret: "" });
  const [wallet, setWallet] = useState(user?.phantom_wallet || "");
  const [mmWallet, setMmWallet] = useState(user?.metamask_wallet || "");
  const [loadingB, setLoadingB] = useState(false);
  const [loadingC, setLoadingC] = useState(false);
  const [loadingP, setLoadingP] = useState(false);
  const [loadingM, setLoadingM] = useState(false);

  const saveBinance = async (e) => {
    e.preventDefault();
    setLoadingB(true);
    try {
      await client.post("/settings/binance", binance);
      await refresh();
      toast.success("Binance API saved");
      setBinance({ api_key: "", api_secret: "" });
    } catch (e) { toast.error("Failed to save"); } finally { setLoadingB(false); }
  };

  const disconnectBinance = async () => {
    try { await client.delete("/settings/binance"); await refresh(); toast.success("Disconnected"); } catch (e) { /* noop */ }
  };

  const saveCoinbase = async (e) => {
    e.preventDefault();
    setLoadingC(true);
    try {
      await client.post("/settings/coinbase", coinbase);
      await refresh();
      toast.success("Coinbase API saved");
      setCoinbase({ api_key: "", api_secret: "" });
    } catch (e) { toast.error("Failed to save"); } finally { setLoadingC(false); }
  };

  const disconnectCoinbase = async () => {
    try { await client.delete("/settings/coinbase"); await refresh(); toast.success("Disconnected"); } catch (e) { /* noop */ }
  };

  const savePhantom = async (e) => {
    e.preventDefault();
    setLoadingP(true);
    try {
      await client.post("/settings/phantom", { wallet_address: wallet });
      await refresh();
      toast.success("Phantom wallet linked");
    } catch (e) { toast.error("Failed"); } finally { setLoadingP(false); }
  };

  const connectPhantom = async () => {
    if (!window.solana || !window.solana.isPhantom) {
      toast.error("Phantom wallet not detected. Paste address manually.");
      return;
    }
    try {
      const resp = await window.solana.connect();
      const addr = resp.publicKey.toString();
      setWallet(addr);
      await client.post("/settings/phantom", { wallet_address: addr });
      await refresh();
      toast.success("Phantom connected: " + addr.slice(0, 6) + "..." + addr.slice(-4));
    } catch (e) { toast.error("Phantom rejected connection"); }
  };

  const saveMetaMask = async (e) => {
    e.preventDefault();
    setLoadingM(true);
    try {
      await client.post("/settings/metamask", { wallet_address: mmWallet });
      await refresh();
      toast.success("MetaMask wallet linked");
    } catch (e) { toast.error("Failed"); } finally { setLoadingM(false); }
  };

  const connectMetaMask = async () => {
    if (!window.ethereum || !window.ethereum.isMetaMask) {
      toast.error("MetaMask not detected. Paste address manually.");
      return;
    }
    try {
      const accounts = await window.ethereum.request({ method: "eth_requestAccounts" });
      const addr = accounts?.[0];
      if (!addr) { toast.error("No account returned"); return; }
      setMmWallet(addr);
      await client.post("/settings/metamask", { wallet_address: addr });
      await refresh();
      toast.success("MetaMask connected: " + addr.slice(0, 6) + "..." + addr.slice(-4));
    } catch (e) { toast.error("MetaMask rejected connection"); }
  };

  return (
    <div data-testid="settings-page" className="max-w-[1200px] mx-auto px-6 py-12">
      <div className="text-[10px] uppercase tracking-widest text-[#CCFF00] font-mono">Connections</div>
      <h1 className="font-display text-3xl md:text-5xl font-bold mt-2">{t("settings_title")}</h1>

      <div className="mt-10 grid md:grid-cols-2 gap-px bg-[#222] border border-[#222]">
        {/* Binance */}
        <div className="bg-[#0A0A0A] p-8">
          <div className="flex items-center gap-3">
            <Key size={20} className="text-[#CCFF00]" />
            <h3 className="font-display text-xl font-bold">{t("binance_title")}</h3>
            {user?.has_binance && <span className="text-[10px] font-mono bg-[#CCFF00]/10 text-[#CCFF00] px-2 py-0.5">{t("connected")}</span>}
          </div>
          <p className="text-white/50 text-sm mt-3">{t("binance_desc")}</p>
          {user?.has_binance ? (
            <button onClick={disconnectBinance} data-testid="binance-disconnect-button" className="mt-6 btn-ghost flex items-center gap-2"><X size={14} /> {t("disconnect")}</button>
          ) : (
            <form onSubmit={saveBinance} className="mt-6 space-y-4">
              <Field label={t("api_key")} value={binance.api_key} onChange={(v) => setBinance({ ...binance, api_key: v })} testid="binance-key-input" />
              <Field label={t("api_secret")} type="password" value={binance.api_secret} onChange={(v) => setBinance({ ...binance, api_secret: v })} testid="binance-secret-input" />
              <button disabled={loadingB} data-testid="binance-save-button" className="btn-acid w-full">{loadingB ? "…" : t("save")}</button>
            </form>
          )}
        </div>

        {/* Coinbase */}
        <div className="bg-[#0A0A0A] p-8">
          <div className="flex items-center gap-3">
            <Landmark size={20} className="text-[#CCFF00]" />
            <h3 className="font-display text-xl font-bold">{t("coinbase_title")}</h3>
            {user?.has_coinbase && <span className="text-[10px] font-mono bg-[#CCFF00]/10 text-[#CCFF00] px-2 py-0.5">{t("connected")}</span>}
          </div>
          <p className="text-white/50 text-sm mt-3">{t("coinbase_desc")}</p>
          {user?.has_coinbase ? (
            <button onClick={disconnectCoinbase} data-testid="coinbase-disconnect-button" className="mt-6 btn-ghost flex items-center gap-2"><X size={14} /> {t("disconnect")}</button>
          ) : (
            <form onSubmit={saveCoinbase} className="mt-6 space-y-4">
              <Field label={t("api_key")} value={coinbase.api_key} onChange={(v) => setCoinbase({ ...coinbase, api_key: v })} testid="coinbase-key-input" />
              <Field label={t("api_secret")} type="password" value={coinbase.api_secret} onChange={(v) => setCoinbase({ ...coinbase, api_secret: v })} testid="coinbase-secret-input" />
              <button disabled={loadingC} data-testid="coinbase-save-button" className="btn-acid w-full">{loadingC ? "…" : t("save")}</button>
            </form>
          )}
        </div>

        {/* Phantom */}
        <div className="bg-[#0A0A0A] p-8">
          <div className="flex items-center gap-3">
            <WalletIcon size={20} className="text-[#CCFF00]" />
            <h3 className="font-display text-xl font-bold">{t("phantom_title")}</h3>
            {user?.phantom_wallet && <span className="text-[10px] font-mono bg-[#CCFF00]/10 text-[#CCFF00] px-2 py-0.5">{t("connected")}</span>}
          </div>
          <p className="text-white/50 text-sm mt-3">{t("phantom_desc")}</p>

          {user?.phantom_wallet && (
            <div className="mt-6 surface-elev p-4 font-mono text-[10px] break-all">
              <div className="text-white/40 text-[9px] uppercase mb-1">Linked wallet</div>
              {user.phantom_wallet}
            </div>
          )}

          <button onClick={connectPhantom} data-testid="phantom-connect-button" className="mt-6 btn-acid w-full flex items-center justify-center gap-2">
            <Check size={14} /> Connect Phantom
          </button>

          <form onSubmit={savePhantom} className="mt-6 space-y-4">
            <Field label={t("wallet_address")} value={wallet} onChange={setWallet} testid="phantom-address-input" />
            <button disabled={loadingP} data-testid="phantom-save-button" className="btn-ghost w-full">{loadingP ? "…" : t("save")}</button>
          </form>
        </div>

        {/* MetaMask */}
        <div className="bg-[#0A0A0A] p-8">
          <div className="flex items-center gap-3">
            <Link2 size={20} className="text-[#CCFF00]" />
            <h3 className="font-display text-xl font-bold">{t("metamask_title")}</h3>
            {user?.metamask_wallet && <span className="text-[10px] font-mono bg-[#CCFF00]/10 text-[#CCFF00] px-2 py-0.5">{t("connected")}</span>}
          </div>
          <p className="text-white/50 text-sm mt-3">{t("metamask_desc")}</p>

          {user?.metamask_wallet && (
            <div className="mt-6 surface-elev p-4 font-mono text-[10px] break-all">
              <div className="text-white/40 text-[9px] uppercase mb-1">Linked wallet</div>
              {user.metamask_wallet}
            </div>
          )}

          <button onClick={connectMetaMask} data-testid="metamask-connect-button" className="mt-6 btn-acid w-full flex items-center justify-center gap-2">
            <Check size={14} /> Connect MetaMask
          </button>

          <form onSubmit={saveMetaMask} className="mt-6 space-y-4">
            <Field label={t("wallet_address")} value={mmWallet} onChange={setMmWallet} testid="metamask-address-input" />
            <button disabled={loadingM} data-testid="metamask-save-button" className="btn-ghost w-full">{loadingM ? "…" : t("save")}</button>
          </form>
        </div>
      </div>

      <div className="mt-10 border border-[#FF3366]/30 bg-[#FF3366]/5 p-6 text-xs text-white/60 font-mono leading-relaxed">
        <span className="text-[#FF3366] font-bold uppercase">SECURITY:</span> {t("security_note")}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text" }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono mb-2">{label}</div>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} required
        className="w-full bg-[#0A0A0A] border border-[#222] focus:border-[#CCFF00] focus:outline-none px-4 py-3 font-mono text-sm" />
    </label>
  );
}
